/**
 * queue/engine.ts
 * The deterministic FITTED allocation engine, ported 1:1 from the verified
 * prototype. Pure functions over (entries, resources): identical inputs →
 * identical output. No randomness, no wall-clock branching. SUKI / the LLM
 * never picks a resource — these functions are the only allocator.
 *
 * Mutation style: the store passes in working copies (it clones state before
 * applying), so these helpers mutate the arrays/objects they're given and the
 * store commits the result. Each transition appends an append-only ledger row.
 */

import type { Entry, Resource, Actor, EntryState, Cfg2 } from './types';

/** Append a ledger row (append-only; never edit/remove prior rows). */
export function appendEvent(entry: Entry, type: EntryState, actor: Actor, at: number, reason?: string): void {
  entry.events = [...entry.events, { type, actor, at, reason }];
  entry.state = type; // cached projection
}

/** WAITING entries in FIFO order (earliest joinAt first). */
export function waitingFIFO(entries: Entry[]): Entry[] {
  return entries.filter(e => e.state === 'WAITING').sort((a, b) => a.joinAt - b.joinAt);
}

/** 1-based position of an entry within the WAITING line, or null. */
export function positionOf(entries: Entry[], id: string): number | null {
  const f = waitingFIFO(entries);
  const i = f.findIndex(e => e.id === id);
  return i < 0 ? null : i + 1;
}

export function freeResources(resources: Resource[]): Resource[] {
  return resources.filter(r => r.status === 'free');
}

/**
 * FITTED best fit: smallest free resource with cap ≥ party, tie-broken by id.
 * This is the deterministic core — exclusive consume, one party per resource.
 */
export function bestFit(resources: Resource[], party: number): Resource | null {
  const fits = freeResources(resources)
    .filter(r => r.cap >= party)
    .sort((a, b) => a.cap - b.cap || a.id.localeCompare(b.id));
  return fits[0] || null;
}

/** Seat an entry into a resource (occupied) and log it. */
export function seat(entry: Entry, res: Resource, actor: Actor, at: number): void {
  res.status = 'occupied';
  res.occupant = entry.id;
  res.heldFor = null;
  entry.allocatedResource = res.id;
  if (actor === 'STAFF') appendEvent(entry, 'ALLOCATED', 'STAFF', at);
  appendEvent(entry, 'SEATED', actor, at);
}

// ── Outcome types returned to the store so it can toast + commit ───────────
export interface EngineResult {
  ok: boolean;
  toastTag: string;
  toastMsg: string;
}

/** Auto-seat: engine picks the smallest fit and seats immediately. */
export function runAutoSeat(entries: Entry[], resources: Resource[], entryId: string, at: number): EngineResult {
  const e = entries.find(x => x.id === entryId);
  if (!e) return { ok: false, toastTag: 'ERR', toastMsg: 'Entry not found' };
  const fit = bestFit(resources, e.party);
  if (!fit) return { ok: false, toastTag: 'HOLD', toastMsg: `No free table fits party of ${e.party} — ${e.displayName} is holding` };
  seat(e, fit, 'STAFF', at);
  return { ok: true, toastTag: 'SEATED', toastMsg: `${e.displayName} → ${fit.label} (engine pick · smallest fit)` };
}

/** Staff pick: seat the selected entry into a chosen free resource (validated). */
export function runPickResource(
  entries: Entry[], resources: Resource[], entryId: string, resId: string, cfg2: Cfg2, at: number,
): EngineResult {
  const e = entries.find(x => x.id === entryId);
  const r = resources.find(x => x.id === resId);
  if (!e || !r) return { ok: false, toastTag: 'ERR', toastMsg: 'Not found' };
  if (r.status !== 'free') return { ok: false, toastTag: 'BLOCKED', toastMsg: `${r.label} is not free` };
  if (r.cap < e.party && !(r.shared && cfg2.shared_seating)) {
    return { ok: false, toastTag: 'TOO SMALL', toastMsg: `${r.label} seats ${r.cap}, party is ${e.party}` };
  }
  seat(e, r, 'STAFF', at);
  return { ok: true, toastTag: 'SEATED', toastMsg: `${e.displayName} → ${r.label} (staff pick)` };
}

/**
 * Resource freed via Billing settle → free it, then allocate the front of the
 * FIFO line to the first fit (CALLED + table HELD). Mirrors
 * `order.settled → resource.freed → allocate`.
 */
export function runSettle(entries: Entry[], resources: Resource[], resId: string, orderNo: number, at: number): EngineResult {
  const r = resources.find(x => x.id === resId);
  if (!r) return { ok: false, toastTag: 'ERR', toastMsg: 'Table not found' };
  const occ = entries.find(e => e.id === r.occupant);
  if (occ) appendEvent(occ, 'RELEASED', 'SYSTEM', at, 'order.settled');
  r.status = 'free';
  r.occupant = null;
  let msg = `${r.label} freed (Billing settled · order #${orderNo})`;
  for (const e of waitingFIFO(entries)) {
    const fit = bestFit(resources, e.party);
    if (fit) {
      fit.status = 'held';
      fit.heldFor = e.id;
      e.allocatedResource = fit.id;
      appendEvent(e, 'CALLED', 'SYSTEM', at, 'resource.freed');
      msg += ` → ${e.displayName} called to ${fit.label}`;
      break;
    }
  }
  return { ok: true, toastTag: 'AUTO', toastMsg: msg };
}

/** Manual "Call next": call the first FIFO entry that fits. */
export function runCallNext(entries: Entry[], resources: Resource[], at: number): EngineResult {
  for (const e of waitingFIFO(entries)) {
    const fit = bestFit(resources, e.party);
    if (fit) {
      fit.status = 'held';
      fit.heldFor = e.id;
      e.allocatedResource = fit.id;
      appendEvent(e, 'CALLED', 'STAFF', at);
      return { ok: true, toastTag: 'CALLED', toastMsg: `${e.displayName} → ${fit.label}` };
    }
  }
  return { ok: false, toastTag: 'HOLD', toastMsg: 'No free table fits the front of the line' };
}

/** Confirm a CALLED entry into its held table. */
export function runSeatCalled(entries: Entry[], resources: Resource[], entryId: string, at: number): EngineResult {
  const e = entries.find(x => x.id === entryId);
  if (!e || !e.allocatedResource) return { ok: false, toastTag: 'ERR', toastMsg: 'No held table' };
  const r = resources.find(x => x.id === e.allocatedResource);
  if (!r) return { ok: false, toastTag: 'ERR', toastMsg: 'Table not found' };
  seat(e, r, 'STAFF', at);
  return { ok: true, toastTag: 'SEATED', toastMsg: `${e.displayName} seated at ${r.label}` };
}

/** No-show: terminal NO_SHOW, free any held table, optional charge note. */
export function runNoShow(entries: Entry[], resources: Resource[], entryId: string, cfg2: Cfg2, at: number): EngineResult {
  const e = entries.find(x => x.id === entryId);
  if (!e) return { ok: false, toastTag: 'ERR', toastMsg: 'Entry not found' };
  if (e.allocatedResource) {
    const r = resources.find(x => x.id === e.allocatedResource);
    if (r && r.status === 'held') { r.status = 'free'; r.heldFor = null; }
    e.allocatedResource = null;
  }
  appendEvent(e, 'NO_SHOW', 'STAFF', at, 'no-show after grace');
  let msg = `${e.displayName} marked no-show`;
  if (cfg2.no_show) msg += ` · ₹${Number(cfg2.noShowAmt).toLocaleString('en-IN')} charged (Billing)`;
  return { ok: true, toastTag: 'NO_SHOW', toastMsg: msg };
}
